import type { PortalWaiverDocumentId } from './waivers';
import { isLevel, levelIndex, splitLevel, type Level } from './levels';

export const TOURNAMENT_REGISTRATION_STATES = [
  'Not Open',
  'Open',
  'Closed',
] as const;
export const TOURNAMENT_PRICING = {
  baseCents: 32_500,
  includedPlayers: 7,
  additionalPlayerCents: 1_500,
} as const;

/** The player levels each tournament level fits, lowest to highest; an enforced range blocks submission, the others only warn. */
const TOURNAMENT_LEVEL_RANGE = {
  Recreational: { lowest: 'B', highest: 'BB+', enforced: true },
  Intermediate: { lowest: 'BB+', highest: 'A+', enforced: false },
} as const satisfies Record<
  Tournament['level'],
  { lowest: Level; highest: Level; enforced: boolean }
>;

/** The league season or clinic term that sets a player's tournament level. */
export interface LevelPlay {
  /** Such as `Winter 2026`. */
  term: string;
  /** A league flavour, a clinic name, or the override's label. */
  what: string;
  kind: 'league' | 'clinic' | 'override';
  level: Level;
  /** YYYY-MM-DD. */
  startedOn: string;
}

export interface Tournament {
  id: string;
  name: string;
  code: string;
  startsOn: string;
  level: 'Recreational' | 'Intermediate';
  venue: string;
  infoUrl: string | null;
  registrationState: (typeof TOURNAMENT_REGISTRATION_STATES)[number];
  rosterSize: number;
  maxRosterSize: number;
  waiverDocument: PortalWaiverDocumentId | null;
}

export const TOURNAMENT_COPY = {
  noWaiver:
    'The tournament waiver isn’t ready yet. You can still submit your team for review.',
  title: 'Tournaments',
  homeDescription: 'Rally your team for a good cause',
  empty: 'No tournaments are available yet.',
  loading: 'Loading tournaments…',
  loadError: 'We couldn’t load tournaments. Try again.',
  unavailable: 'This tournament is no longer available.',
  noAccess: 'Tournament registration isn’t available for this account yet.',
  notOpenYet: 'Registration isn’t open yet.',
  closed: 'Registration is closed.',
  oneTournament: 'You can only register for one tournament at a time.',
  register: 'Register a team',
  edit: 'Edit team',
  rosterSummary: (count: number, min: number) =>
    `${count} ${count === 1 ? 'player' : 'players'} (min ${min})`,
  rosterRange: (min: number, max: number) => `${min}–${max} players`,
  rosterMinimum: (min: number) => `Your team needs at least ${min} players.`,
  rosterMaximum: (max: number) => `Your team can have up to ${max} players.`,
  memberMinimum: 'At least 66% BOBA / FTLO players',
  fullMembership:
    'Every player on your roster must qualify as an FTLO/BOBA member.',
  missingAccount:
    'We couldn’t find an account with that email. Ask this player to create an FTLO account first.',
  otherTeam:
    'A player is already on another tournament team. Remove them to save or submit.',
  onTeam: (team: string) => `Already on ${team} for an upcoming tournament`,
  noOtherTeam: 'Not on another tournament team',
  invalidSubmission: 'Check your team details and try again.',
  oneCaptain: 'Give exactly one roster player the captain role.',
  levelRequirement:
    'Every player must be eligible for this tournament’s level.',
  levelBlocker: 'Check the highlighted players’ levels before you submit.',
  belowLevel:
    'This player’s FTLO history puts them below BB+. Double-check they are comfortable playing at this level.',
  aboveLevel:
    'Based on this player’s FTLO playing history, their level is above the Recreational tournament.',
  saved: 'Team saved.',
  submitted: 'Team submitted for review.',
  saveError: 'We couldn’t save your team. Try again.',
  left: 'You’ve left the team.',
  leaveError: 'We couldn’t remove you from the team. Try again.',
  moved: (level: string) => `Your team moved to ${level}.`,
  moveError: 'We couldn’t move your team. Try again.',
  withdraw: 'Withdraw team',
  withdrawTitle: 'Withdraw this team?',
  withdrawBody: (team: string) =>
    `${team} will come out of the tournament. Everyone on the roster is free to join or start another team. This can’t be undone.`,
  withdrawCancel: 'Keep the team',
  withdrawn: 'You’ve withdrawn the team.',
  withdrawError: 'We couldn’t withdraw the team. Try again.',
  leaveDescription: 'You’ll be removed from this tournament’s roster.',
} as const;

export function tournamentInfoUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function tournamentFeeCents(playerCount: number): number {
  const { baseCents, includedPlayers, additionalPlayerCents } =
    TOURNAMENT_PRICING;
  return (
    baseCents +
    Math.max(0, playerCount - includedPlayers) * additionalPlayerCents
  );
}

/** How a player's level, such as `BB` or `BB - Matcha`, misses the tournament's range, and whether that blocks submission; undefined when it fits or is unknown. */
export function tournamentLevelIssue(
  highestLevel: string,
  tournamentLevel: Tournament['level'],
):
  | { direction: 'below' | 'above'; message: string; blocks: boolean }
  | undefined {
  const { level } = splitLevel(highestLevel);
  if (!isLevel(level)) return undefined;
  const { lowest, highest, enforced } = TOURNAMENT_LEVEL_RANGE[tournamentLevel];
  if (levelIndex(level) < levelIndex(lowest))
    return {
      direction: 'below',
      message: TOURNAMENT_COPY.belowLevel,
      blocks: enforced,
    };
  if (levelIndex(level) > levelIndex(highest))
    return {
      direction: 'above',
      message: TOURNAMENT_COPY.aboveLevel,
      blocks: enforced,
    };
  return undefined;
}
